export type SupplierListFilters = {
  riskBand?: 'high' | 'medium' | 'low'
  categoryHint?: string | null
  /** Match supplier.sub_category instead of category. */
  subCategoryHint?: string | null
  /** Display label for geo filter (e.g. USA, EMEA, Germany). */
  regionHint?: string | null
  /** When set, match supplier.location only (country asks). */
  geoKind?: 'country' | 'region'
  /** Country / location substrings matched against supplier.location. */
  locationHints?: string[]
  nameHint?: string | null
  /** Single-source / sole-source suppliers only. */
  singleSource?: boolean
  /**
   * Direct / Indirect master tag. When unset, list/count/group default to Direct
   * (same as Risk Overview / Intelligence View). Use `all` for both.
   */
  procurementType?: 'direct' | 'indirect' | 'all'
  /** Dimension score threshold (e.g. financial > 5). */
  scoreFilter?: {
    dim: string
    op: '>' | '>=' | '<' | '<='
    value: number
  }
  /**
   * When set, list and highest-score answers include only these supplier ids.
   * Used by Category Analyst so a brief does not search the rest of the roster.
   */
  supplierIds?: ReadonlySet<string>
  /** Overall score threshold (e.g. "above 5"). */
  overallScoreMin?: number
  /** When true with overallScoreMin, use exclusive > instead of >=. */
  overallScoreExclusive?: boolean
}

/** Effective procurement scope for RA tables (defaults to Direct). */
export function effectiveProcurementType(
  filters: Pick<SupplierListFilters, 'procurementType'>
): 'direct' | 'indirect' | 'all' {
  return filters.procurementType ?? 'direct'
}

export type ListTableIntent =
  | 'active_mitigations'
  | 'alerts'
  | 'intelligence'
  | 'mitigation_tracker'
  | 'special_reports'

const REGION_CANONICAL: Record<string, string> = {
  'north america': 'NA',
  americas: 'NA',
  amer: 'NA',
  na: 'NA',
  latam: 'LATAM',
  'latin america': 'LATAM',
  emea: 'EMEA',
  europe: 'EMEA',
  apac: 'APAC',
  asia: 'APAC',
}

/** Short forms → canonical country location strings used in DB. */
const COUNTRY_ALIASES: Record<string, string[]> = {
  usa: ['united states', 'usa', 'u.s.', 'u.s.a.'],
  us: ['united states', 'usa', 'u.s.', 'u.s.a.'],
  'u.s.': ['united states', 'usa', 'u.s.', 'u.s.a.'],
  'u.s.a.': ['united states', 'usa', 'u.s.', 'u.s.a.'],
  'united states': ['united states', 'usa', 'u.s.', 'u.s.a.'],
  uk: ['united kingdom'],
  'u.k.': ['united kingdom'],
  'united kingdom': ['united kingdom'],
  britain: ['united kingdom'],
  'great britain': ['united kingdom'],
  korea: ['south korea'],
  'south korea': ['south korea'],
  'republic of korea': ['south korea'],
}

function isNarrativeChatAsk(t: string): boolean {
  return (
    t.includes('next best') ||
    t.includes('questions should') ||
    t.includes('what questions') ||
    t.includes('know about') ||
    t.includes('markdown table') ||
    t.includes('help me know')
  )
}

function wantsListTable(text: string): boolean {
  const t = text.toLowerCase()
  if (isNarrativeChatAsk(t)) return false
  return (
    t.includes('table') ||
    t.includes('list') ||
    /\bshow (me )?(all|the|active|recent|high)\b/.test(t) ||
    /\bgive me (all|the|a|an)\b/.test(t) ||
    /\bdisplay\b/.test(t) ||
    /\bfind (me )?(all |the )?\b/.test(t) ||
    /\bwhich suppliers\b/.test(t) ||
    /\bcopy (visible|all|table)\b/.test(t) ||
    /\b(what|who) are (the )?(high|medium|low)[- ]?risk suppliers\b/.test(t) ||
    /\b(what|who) are the .+suppliers\b/.test(t) ||
    /\bwho (are|is)\b.+\bsuppliers?\b/.test(t)
  )
}

function isActiveMitigationsAsk(t: string): boolean {
  if (!t.includes('mitigation')) return false
  if (t.includes('tracker')) return false
  return (
    t.includes('active') ||
    t.includes('dashboard') ||
    (wantsListTable(t) && !t.includes('alert'))
  )
}

/** “What’s new” / “latest risk” — the week’s alerts, not a named-supplier urgent ask. */
export function isWhatsNewAsk(text: string): boolean {
  const t = text.toLowerCase().replace(/\s+/g, ' ').trim()
  if (/\burgent/.test(t) && /\b(on|for|about)\b/.test(t)) return false
  return (
    /\bwhat'?s new\b/.test(t) ||
    /\bwhats new\b/.test(t) ||
    /\bwhat is new\b/.test(t) ||
    /\bnew today\b/.test(t) ||
    /\blatest risk\b/.test(t)
  )
}

/** “Highest risk in MCC” / “highest risk in chemicals” — not a supplier list. */
export function isHighestRiskQuestion(text: string): boolean {
  const t = text.toLowerCase().replace(/\s+/g, ' ').trim()
  if (!/\b(highest|top)\b/.test(t) || !/\brisk\b/.test(t)) return false
  if (/\bsuppliers\b/.test(t)) return false
  return /\b(in|for|of|on)\s+\S+/.test(t)
}

export function isAlertsAsk(t: string): boolean {
  if (t.includes('mitigation') && t.includes('alert')) return false
  if (isWhatsNewAsk(t)) return true
  return (
    (t.includes('alert') && wantsListTable(t)) ||
    (t.includes('recent') && (t.includes('alert') || t.includes('signal') || t.includes('news'))) ||
    (t.includes('news') && wantsListTable(t) && !t.includes('supplier'))
  )
}

function isMitigationTrackerAsk(t: string): boolean {
  if (t.includes('mitigation tracker')) return true
  if (t.includes('mitigation') && t.includes('tracker')) return true
  if (t.includes('mitigation') && t.includes('alert') && wantsListTable(t)) return true
  return false
}

function isSpecialReportsAsk(t: string): boolean {
  if (t.includes('special report')) return true
  if (!wantsListTable(t)) return false
  return t.includes('report') && !t.includes('risk assessment')
}

function isIntelligenceListAsk(t: string): boolean {
  if (
    (t.includes('risk assessment') || t.includes('risk rating') || t.includes('scorecard')) &&
    !t.includes('supplier') &&
    !t.includes('high') &&
    !t.includes('medium') &&
    !t.includes('low')
  ) {
    return false
  }

  if (
    /\b(high|medium|low)[- ]?risk suppliers\b/.test(t) ||
    /\bsuppliers in\b/.test(t)
  ) {
    return true
  }

  if (!wantsListTable(t)) return false
  return (
    t.includes('intelligence') ||
    t.includes('risk matrix') ||
    t.includes('suppliers') ||
    t.includes('high-risk') ||
    t.includes('high risk') ||
    t.includes('medium risk') ||
    t.includes('low risk') ||
    (t.includes('supplier') && (t.includes('list') || t.includes('table')))
  )
}

function titleCaseCountry(raw: string): string {
  return raw
    .split(/\s+/)
    .map((w) => {
      if (/^(uk|usa|u\.s\.a\.|u\.s\.|u\.k\.)$/i.test(w)) return w.toUpperCase().replace(/\./g, '.')
      return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
    })
    .join(' ')
}

/**
 * Pull a place phrase after "in …" (e.g. suppliers in Germany / high risk in France).
 */
export function extractPlaceAfterIn(text: string): string | null {
  const t = text.replace(/\s+/g, ' ').trim()
  const m = t.match(/\bin\s+(.+)$/i)
  if (!m?.[1]) return null
  let place = m[1].trim()
  place = place.replace(/[?.!,;:]+$/g, '').trim()
  place = place
    .replace(/\s+(in\s+a\s+table|as\s+a\s+table|table|list|please|thanks)\b.*$/i, '')
    .trim()
  if (place.length < 2) return null
  // Ignore if the "in" was part of something else with only risk words
  if (/^(a table|table|the table)$/i.test(place)) return null
  // Ignore portfolio/meta phrases ("in this database", "in our data")
  if (
    /^(this\s+)?(database|db|platform|system|portfolio|roster|dataset)$/i.test(place) ||
    /^(the\s+)?(database|db|platform|system|portfolio|roster|dataset)$/i.test(place) ||
    /^(our|the|your)\s+(database|data|platform|system|portfolio|roster)$/i.test(place) ||
    /^(total|all|general)$/i.test(place)
  ) {
    return null
  }
  return place
}

function applyGeoFromPlace(placeRaw: string, filters: SupplierListFilters): void {
  const place = placeRaw.toLowerCase().trim()
  if (!place) return

  // Regions first
  const regionKey = REGION_CANONICAL[place]
  if (regionKey) {
    filters.geoKind = 'region'
    filters.regionHint = regionKey
    return
  }

  // USA / United States (also caught via bare place)
  if (
    place === 'usa' ||
    place === 'us' ||
    place === 'u.s.' ||
    place === 'u.s.a.' ||
    place === 'united states'
  ) {
    filters.geoKind = 'country'
    filters.regionHint = 'USA'
    filters.locationHints = COUNTRY_ALIASES.usa
    return
  }

  const aliases = COUNTRY_ALIASES[place]
  if (aliases) {
    filters.geoKind = 'country'
    filters.regionHint = titleCaseCountry(aliases[0])
    filters.locationHints = aliases
    return
  }

  // Generic country: match supplier.location containing this phrase
  filters.geoKind = 'country'
  filters.regionHint = titleCaseCountry(place)
  filters.locationHints = [place]
}

/** Explicit category / industry phrases (before geo "in …"). */
import { SUBCATEGORIES_BY_CATEGORY } from './riskIntakeTaxonomy'

const KNOWN_CATEGORY_TOKENS = [
  ...Object.keys(SUBCATEGORIES_BY_CATEGORY).map((k) => k.toLowerCase()),
  'external manuf',
  'external manufacturing',
]

const SUBCATEGORY_TOKENS = Object.values(SUBCATEGORIES_BY_CATEGORY)
  .flat()
  .map((s) => s.toLowerCase())

/** Category key for a phrase such as "chemicals" or "packaging". */
export function categoryKeyForPhrase(phrase: string): string | null {
  const h = phrase.toLowerCase().trim()
  if (!h) return null
  const keys = Object.keys(SUBCATEGORIES_BY_CATEGORY)
  const exact = keys.find((k) => k.toLowerCase() === h)
  if (exact) return exact
  return (
    keys.find((k) => {
      const kl = k.toLowerCase()
      return h === `${kl}s` || kl === `${h}s`
    }) ?? null
  )
}

export function subcategoryKeyForPhrase(phrase: string): string | null {
  const h = phrase.toLowerCase().trim()
  for (const subs of Object.values(SUBCATEGORIES_BY_CATEGORY)) {
    const exact = subs.find((s) => s.toLowerCase() === h)
    if (exact) return exact
  }
  return null
}

function splitTaxonomyHint(
  hint: string,
  text: string
): { categoryHint?: string; subCategoryHint?: string } {
  const wantsSub = /\bsub[- ]?categor/.test(text.toLowerCase())
  const cat = categoryKeyForPhrase(hint)
  const sub = subcategoryKeyForPhrase(hint)
  if (wantsSub) return { subCategoryHint: sub || hint }
  if (sub && !cat) return { subCategoryHint: sub }
  if (cat) return { categoryHint: cat }
  return { categoryHint: hint }
}

/** Category match, or subcategory when that is what was asked or the category slice is empty. */
export function filterByCategoryScope<
  T extends { category?: string | null; industry?: string | null; sub_category?: string | null },
>(
  list: T[],
  filters: Pick<SupplierListFilters, 'categoryHint' | 'subCategoryHint'>
): { rows: T[]; usedSubcategory: boolean } {
  if (filters.subCategoryHint) {
    const q = filters.subCategoryHint.toLowerCase()
    return {
      rows: list.filter((s) => (s.sub_category || '').toLowerCase().includes(q)),
      usedSubcategory: true,
    }
  }
  if (!filters.categoryHint) return { rows: list, usedSubcategory: false }
  const q = filters.categoryHint.toLowerCase()
  const byCat = list.filter((s) => (s.category || s.industry || '').toLowerCase().includes(q))
  if (byCat.length > 0) return { rows: byCat, usedSubcategory: false }
  const bySub = list.filter((s) => (s.sub_category || '').toLowerCase().includes(q))
  if (bySub.length > 0) return { rows: bySub, usedSubcategory: true }
  return { rows: [], usedSubcategory: false }
}

export function emptyMatchSentence(title: string): string {
  const subject = title.replace(/\s+/g, ' ').trim().replace(/\.+$/, '')
  let next = 'Next question: “Tell me about a supplier by name.”'
  if (/alert/i.test(title)) {
    next = 'Next question: “What should I look at for a named supplier?”'
  } else if (/mitigation/i.test(title)) {
    next = 'Next question: “Open mitigations for a supplier by name.”'
  } else if (/packaging|subcategor|intelligence view|categor/i.test(title)) {
    next = 'Next question: “Which subcategory should I open?”'
  }
  return `Nothing in platform data matched ${subject}. ${next}`
}

export function newlyHighSuppliersSentence(): string {
  return 'No newly high suppliers were found in the past week. No earlier score is stored to compare against this week.'
}

/** “I am the chemicals manager” → Chemicals. */
export function categoryFromRoleAsk(text: string): string | null {
  const m = text.match(/\bi(?:\s+am|'m)\s+the\s+(.+?)\s+manager\b/i)
  if (!m?.[1]) return null
  const phrase = m[1].trim()
  return categoryKeyForPhrase(phrase) || phrase
}

/** Tail of “highest risk in …” when that question is not a supplier list. */
export function highestRiskScope(text: string): string | null {
  if (!isHighestRiskQuestion(text)) return null
  const m = text.match(/\b(?:in|for|of|on)\s+(.+?)(?:[?.!]|$)/i)
  if (!m?.[1]) return null
  return m[1].replace(/[?.!,;:]+$/g, '').trim()
}

function extractCategoryHint(text: string): string | null {
  const t = text.replace(/\s+/g, ' ').trim()
  const patterns = [
    /\bin\s+(?:the\s+)?([a-z0-9][\w\s&/-]{1,40}?)\s+categor(?:y|ies)\b/i,
    /\bcategor(?:y|ies)\s*[:=]?\s+([a-z0-9][\w\s&/-]{1,40}?)(?:\s+(?:in|with|for|table|list|please)|[.?,!]|$)/i,
    /\bindustry\s*[:=]?\s+([a-z0-9][\w\s&/-]{1,40}?)(?:\s+(?:in|with|for|table|list|please)|[.?,!]|$)/i,
    // Adjective before suppliers: "high risk packaging suppliers"
    /\b([a-z][\w\s&/-]{1,40}?)\s+suppliers?\b/i,
    // Refine: "only packaging"
    /^(?:now|only|just)\s+([a-z][\w\s&/-]{1,40}?)$/i,
  ]
  for (const re of patterns) {
    const m = t.match(re)
    if (!m?.[1]) continue
    let hint = m[1].trim().replace(/[?.!,;:]+$/g, '').trim()
    hint = hint
      .replace(/^(high|medium|low)[- ]?risk\s+/i, '')
      .replace(/\b(high|medium|low)[- ]?risk\b/gi, '')
      .replace(/\bsingle[- ]source\b/gi, '')
      .replace(/\bsole[- ]source\b/gi, '')
      .replace(/\b(direct|indirect)(\s+procurement)?\b/gi, '')
      .replace(/\s+(suppliers?|table|list|please)$/i, '')
      .replace(/^(the|a|an|all|those|these)\s+/i, '')
      .trim()
    if (
      hint.length < 2 ||
      /^(high|medium|low|risk|single|sole|source|emea|apac|latam|na|usa|us|direct|indirect|procurement)$/i.test(
        hint
      ) ||
      REGION_CANONICAL[hint.toLowerCase()] ||
      COUNTRY_ALIASES[hint.toLowerCase()]
    ) {
      continue
    }
    // Adjective / "only X" forms: prefer known category tokens to avoid geo false positives
    if (re.source.includes('suppliers') || re.source.startsWith('^(?:now|only|just)')) {
      const hl = hint.toLowerCase()
      const known = KNOWN_CATEGORY_TOKENS.find((k) => hl === k || hl.includes(k) || k.includes(hl))
      const subKnown = SUBCATEGORY_TOKENS.includes(hl)
      if (!known && !subKnown && !/\bcategor/i.test(t) && !/\bindustry/i.test(t)) {
        // Still accept short refine "only packaging" when it matches a known token partially
        if (
          !KNOWN_CATEGORY_TOKENS.some((k) => k.startsWith(hl) || hl.startsWith(k.split(' ')[0])) &&
          !SUBCATEGORY_TOKENS.some((k) => k === hl)
        ) {
          continue
        }
      }
      if (known) hint = known
      else if (subKnown) hint = hl
    }
    return hint
  }
  return null
}

function extractScoreThreshold(text: string): {
  dim?: string
  overall?: boolean
  op: '>' | '>=' | '<' | '<='
  value: number
} | null {
  const t = text.toLowerCase().replace(/\s+/g, ' ')
  const m = t.match(
    /\b(?:(financial|fin|ops|operational|legal|compliance|strategic|cyber|esg|labor|labour|geo(?:political)?|infra(?:structure)?|supply[- ]?chain|market|tech(?:nology)?)\s*)?(?:risk\s+|score\s+)?(?:more\s+than|greater\s+than|above|over|at\s+least|>=|>|less\s+than|below|under|at\s+most|<=|<)\s*(\d+(?:\.\d+)?)\b/
  )
  if (!m) {
    // Short refine: "above 5" / "over 6"
    const short = t.match(/^(?:now|only|just)?\s*(?:above|over|more\s+than|greater\s+than|>|>=)\s*(\d+(?:\.\d+)?)\s*$/)
    if (short) {
      return { overall: true, op: '>', value: Number(short[1]) }
    }
    return null
  }
  const rawDim = (m[1] || '').trim()
  const value = Number(m[2])
  if (!Number.isFinite(value)) return null
  const opMatch = t.slice(0, m.index! + m[0].length)
  let op: '>' | '>=' | '<' | '<=' = '>'
  if (/\bat\s+least|>=/.test(opMatch)) op = '>='
  else if (/\bat\s+most|<=/.test(opMatch)) op = '<='
  else if (/\bless\s+than|below|under|</.test(opMatch) && !/more\s+than|greater/.test(opMatch)) op = '<'
  else op = '>'

  if (!rawDim) return { overall: true, op, value }

  const dimMap: Record<string, string> = {
    financial: 'financial',
    fin: 'financial',
    ops: 'operational',
    operational: 'operational',
    legal: 'compliance',
    compliance: 'compliance',
    strategic: 'strategic',
    cyber: 'cyber',
    esg: 'esg',
    labor: 'labor',
    labour: 'labor',
    geo: 'geopolitical',
    geopolitical: 'geopolitical',
    infra: 'infra',
    infrastructure: 'infra',
    'supply chain': 'supply_chain',
    'supply-chain': 'supply_chain',
    market: 'market',
    tech: 'tech_innovation',
    technology: 'tech_innovation',
  }
  const dim = dimMap[rawDim] || dimMap[rawDim.replace(/\s+/g, ' ')]
  if (!dim) return { overall: true, op, value }
  return { dim, op, value }
}

/**
 * Normalize geography phrases into region or country location filters.
 */
export function extractSupplierListFilters(text: string): SupplierListFilters {
  const t = text.toLowerCase()
  const filters: SupplierListFilters = {}

  if (/\bhigh[- ]?risk\b/.test(t)) filters.riskBand = 'high'
  else if (/\bmedium[- ]?risk\b/.test(t) || /\bmoderate risk\b/.test(t)) filters.riskBand = 'medium'
  else if (/\blow[- ]?risk\b/.test(t)) filters.riskBand = 'low'

  if (
    /\bsingle[- ]source\b/.test(t) ||
    /\bsole[- ]source\b/.test(t)
  ) {
    filters.singleSource = true
  }

  if (
    /\b(direct\s+and\s+indirect|indirect\s+and\s+direct|both\s+direct\s+and\s+indirect|all\s+procurement)\b/.test(
      t
    ) ||
    /\b(direct\s*\+\s*indirect|procurement\s+type\s*[:=]?\s*all)\b/.test(t)
  ) {
    filters.procurementType = 'all'
  } else if (
    /\bonly\s+indirect\b/.test(t) ||
    /\bindirect(\s+procurement)?\b/.test(t) ||
    /\bprocurement\s+type\s*[:=]?\s*indirect\b/.test(t)
  ) {
    filters.procurementType = 'indirect'
  } else if (
    /\bonly\s+direct\b/.test(t) ||
    /\bdirect(\s+procurement)?\b/.test(t) ||
    /\bprocurement\s+type\s*[:=]?\s*direct\b/.test(t)
  ) {
    filters.procurementType = 'direct'
  }

  const categoryHint = extractCategoryHint(text)
  if (categoryHint) {
    const split = splitTaxonomyHint(categoryHint, text)
    if (split.subCategoryHint) filters.subCategoryHint = split.subCategoryHint
    else if (split.categoryHint) filters.categoryHint = split.categoryHint
  }

  const scoreTh = extractScoreThreshold(text)
  if (scoreTh) {
    if (scoreTh.dim) {
      filters.scoreFilter = { dim: scoreTh.dim, op: scoreTh.op, value: scoreTh.value }
    } else if (scoreTh.overall && (scoreTh.op === '>' || scoreTh.op === '>=')) {
      filters.overallScoreMin = scoreTh.value
      filters.overallScoreExclusive = scoreTh.op === '>'
    } else if (scoreTh.dim == null && scoreTh.overall && (scoreTh.op === '<' || scoreTh.op === '<=')) {
      // Represent "below N" as scoreFilter on overall via negative path in applyListFilters
      filters.scoreFilter = { dim: 'overall', op: scoreTh.op, value: scoreTh.value }
    }
  }

  const place = extractPlaceAfterIn(text)
  if (place) {
    const placeLower = place.toLowerCase()
    const placeCategory = categoryKeyForPhrase(place)
    const placeSubcategory = subcategoryKeyForPhrase(place)
    if (placeSubcategory && !placeCategory && !filters.subCategoryHint) {
      filters.subCategoryHint = placeSubcategory
    } else if (placeCategory && !filters.categoryHint && !filters.subCategoryHint) {
      filters.categoryHint = placeCategory
    }
    const scopeHint = filters.categoryHint || filters.subCategoryHint
    const placeIsCategoryPhrase =
      Boolean(placeCategory || placeSubcategory || (scopeHint && /categor/i.test(place))) &&
      (!scopeHint ||
        placeLower === scopeHint.toLowerCase() ||
        placeLower.startsWith(`${scopeHint.toLowerCase()} `) ||
        /categor/i.test(place))
    if (!placeIsCategoryPhrase) {
      applyGeoFromPlace(place, filters)
      return filters
    }
  }

  // No usable "in …" place — still allow bare region/USA tokens anywhere in the text
  if (
    /\bunited states\b/.test(t) ||
    /\bu\.?\s*s\.?\s*a\.?\b/.test(t) ||
    /\busa\b/.test(t)
  ) {
    filters.geoKind = 'country'
    filters.regionHint = 'USA'
    filters.locationHints = COUNTRY_ALIASES.usa
  } else if (
    /\bnorth america\b/.test(t) ||
    /\bamericas\b/.test(t) ||
    /\bamer\b/.test(t) ||
    /\bna\b/.test(t)
  ) {
    filters.geoKind = 'region'
    filters.regionHint = 'NA'
  } else if (/\blatin america\b/.test(t) || /\blatam\b/.test(t)) {
    filters.geoKind = 'region'
    filters.regionHint = 'LATAM'
  } else {
    const regionMatch = t.match(/\b(emea|apac|europe|asia)\b/i)
    if (regionMatch) {
      const key = regionMatch[1].toLowerCase()
      filters.geoKind = 'region'
      filters.regionHint = REGION_CANONICAL[key] || regionMatch[1].toUpperCase()
    }
  }

  return filters
}

function locationMatchesHints(location: string, hints: string[]): boolean {
  const loc = location.toLowerCase()
  const locCompact = loc.replace(/\./g, '')
  return hints.some((tok) => {
    const t = tok.toLowerCase()
    if (!t) return false
    if (loc === t || loc.includes(t)) return true
    const compact = t.replace(/\./g, '')
    return compact.length >= 2 && locCompact.includes(compact)
  })
}

/**
 * Whether a supplier matches the geo filters from extractSupplierListFilters.
 */
export function supplierMatchesGeoFilter(
  supplier: { kenvue_region?: string | null; location?: string | null },
  filters: Pick<SupplierListFilters, 'regionHint' | 'locationHints' | 'geoKind'>
): boolean {
  const hint = (filters.regionHint || '').trim()
  const hints = filters.locationHints ?? []
  if (!hint && hints.length === 0) return true

  const region = (supplier.kenvue_region || '').toLowerCase()
  const location = (supplier.location || '').toLowerCase()
  const h = hint.toLowerCase()

  // Country asks: location only (never match solely on kenvue_region)
  if (filters.geoKind === 'country' || h === 'usa') {
    const countryHints =
      hints.length > 0
        ? hints
        : h === 'usa'
          ? COUNTRY_ALIASES.usa
          : [h]
    return locationMatchesHints(location, countryHints)
  }

  if (h === 'na') {
    return (
      region === 'na' ||
      region.includes('north america') ||
      region.includes('amer') ||
      locationMatchesHints(location, hints)
    )
  }

  if (h === 'latam') {
    return region.includes('latam') || region.includes('latin') || location.includes('latin america')
  }

  if (h === 'emea' || h === 'europe') {
    return region.includes('emea') || region.includes('europe')
  }

  if (h === 'apac' || h === 'asia') {
    return region.includes('apac') || region.includes('asia')
  }

  // Generic region token
  return region.includes(h) || (hints.length > 0 && locationMatchesHints(location, hints))
}

/**
 * Brand / product-line list for a named supplier (before scorecard / RAG).
 */
export function isSupplierBrandsAsk(text: string): boolean {
  const t = text.toLowerCase().trim()
  if (!t || isNarrativeChatAsk(t)) return false
  if (!/\bbrands?\b/.test(t)) return false
  return (
    t.includes('associated') ||
    t.includes('list') ||
    t.includes('which') ||
    t.includes('what') ||
    /\bfor\b/.test(t) ||
    /\bof\b/.test(t) ||
    /\bwith\b/.test(t)
  )
}

/** Pure intent match for list tables (after scorecard path). */
export function matchListTableIntent(userText: string): ListTableIntent | null {
  const t = userText.toLowerCase().trim()
  if (!t || isNarrativeChatAsk(t)) return null

  if (isMitigationTrackerAsk(t) && (wantsListTable(t) || t.includes('mitigation tracker'))) {
    return 'mitigation_tracker'
  }
  if (isActiveMitigationsAsk(t) && wantsListTable(t)) return 'active_mitigations'
  if (isSpecialReportsAsk(t)) return 'special_reports'
  if (isAlertsAsk(t)) return 'alerts'
  if (isIntelligenceListAsk(t)) return 'intelligence'
  return null
}
